'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/utils/supabase/client';
import { trackEvent } from '@/lib/analytics';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  company: string;
  industry: string;
  dealDescription: string;
  purchasePrice: string;
  emdAmount: string;
  closingDate: string;
  dealSummary: string;
  howHeard: string;
  promoCode: string;
}

interface UtmData {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  landing_page: string;
  referrer: string;
}

type FormErrors = Partial<Record<keyof FormData, string>>;
type TouchedFields = Partial<Record<keyof FormData, boolean>>;

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_UTM_KEY = 'eb_utm_data';
const SESSION_PROMO_KEY = 'eb_promo_code';
const CRM_ENDPOINT = 'https://app.earnestbridge.com/api/crm/contact-form';

const initialForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  company: '',
  industry: '',
  dealDescription: '',
  purchasePrice: '',
  emdAmount: '',
  closingDate: '',
  dealSummary: '',
  howHeard: '',
  promoCode: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumberWithCommas(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

function validateForm(form: FormData): FormErrors {
  const errors: FormErrors = {};

  if (!form.firstName.trim()) {
    errors.firstName = 'First name is required.';
  }
  if (!form.lastName.trim()) {
    errors.lastName = 'Last name is required.';
  }
  if (!form.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!form.phoneNumber.trim()) {
    errors.phoneNumber = 'Phone number is required.';
  } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(form.phoneNumber.trim())) {
    errors.phoneNumber = 'Please enter a valid phone number.';
  }
  if (!form.industry) {
    errors.industry = 'Please select an industry vertical.';
  }
  if (form.industry && !form.dealDescription) {
    errors.dealDescription = 'Please select a deal type.';
  }
  if (!form.purchasePrice.trim()) {
    errors.purchasePrice = 'Purchase / deal price is required.';
  }
  if (!form.emdAmount.trim()) {
    errors.emdAmount = 'EMD / deposit amount is required.';
  }
  if (!form.closingDate.trim()) {
    errors.closingDate = 'Target close date is required.';
  } else {
    const selected = new Date(form.closingDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selected < today) {
      errors.closingDate = 'Close date must be today or in the future.';
    }
  }

  return errors;
}

// ─── Static data ──────────────────────────────────────────────────────────────

const industryOptions = [
  'Commercial Real Estate',
  'Business Acquisitions (M&A)',
  'Private Aviation',
  'Government & Infrastructure Tendering',
  'Oil, Gas & Mineral Leasing',
  'Luxury Maritime (Yachts)',
  'Other',
];

const dealTypesByIndustry: Record<string, string[]> = {
  'Commercial Real Estate': [
    'Multifamily (5+ units)', 'Office', 'Industrial / Warehouse',
    'Retail / Strip Mall', 'Mixed-Use', 'Hospitality / Hotel',
    'Self-Storage', 'Land / Development', 'Other',
  ],
  'Business Acquisitions (M&A)': [
    'Search Fund Acquisition', 'Owner-Operator Buyout', 'PE Add-On',
    'Management Buyout (MBO)', 'Asset Purchase', 'Other',
  ],
  'Private Aviation': [
    'Cessna Citation', 'Gulfstream', 'Embraer', 'Bombardier', 'Dassault Falcon', 'Other',
  ],
  'Government & Infrastructure Tendering': [
    'Road & Highway', 'Municipal Building', 'Water & Wastewater',
    'School / Education', 'Federal Contract', 'Other',
  ],
  'Oil, Gas & Mineral Leasing': [
    'Mineral Rights Lease', 'Oil & Gas Exploration', 'Upstream Acquisition',
    'Royalty Interest Purchase', 'Acreage Blocking', 'Other',
  ],
  'Luxury Maritime (Yachts)': [
    'Motor Yacht (80ft+)', 'Sailing Yacht', 'Superyacht', 'Commercial Vessel', 'Other',
  ],
  'Other': ['Other'],
};

const dealDescriptionPlaceholders: Record<string, string> = {
  'Commercial Real Estate': 'Briefly describe the property, deal size, and why you need the deposit funded...',
  'Business Acquisitions (M&A)': 'Briefly describe the business, deal size, and the deposit bridge you need...',
  'Private Aviation': 'Briefly describe the aircraft, deal size, and the escrow deposit you need funded...',
  'Government & Infrastructure Tendering': 'Briefly describe the project, contract value, and the tender deposit required...',
  'Oil, Gas & Mineral Leasing': 'Briefly describe the acreage, deal size, and the cash bonus deposit you need funded...',
  'Luxury Maritime (Yachts)': 'Briefly describe the vessel, deal size, and the survey escrow deposit you need funded...',
  'Other': 'Describe your deal and the deposit you need funded...',
};

const howHeardOptions = [
  'Referral from a colleague',
  'Google Search',
  'LinkedIn',
  'Real estate conference',
  'Broker referral',
  'Other',
];

const trustSignals = [
  {
    icon: '⚡',
    title: 'Decisions in 2–4 Hours',
    body: "Our team reviews every submission the same business day. You\'ll never wait days for a response.",
  },
  {
    icon: '🔒',
    title: 'No Personal Guarantee Required',
    body: 'No personal guarantee is required. We evaluate the deal structure, property, and market — your personal assets stay protected.',
  },
  {
    icon: '📋',
    title: 'Simple Agreement',
    body: 'Funding agreement with clear terms. E-sign from anywhere.',
  },
  {
    icon: '🤝',
    title: 'Confidential',
    body: 'Your deal details are never shared with third parties. Strict NDA available on request.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-xs font-medium flex items-center gap-1" style={{ color: '#DC2626' }} role="alert">
      <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  );
}

function inputStyle(hasError: boolean, isTouched: boolean, value: string): React.CSSProperties {
  if (isTouched && hasError) {
    return { borderColor: '#DC2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.12)' };
  }
  if (isTouched && !hasError && value) {
    return { borderColor: 'rgba(82,183,136,0.6)', boxShadow: '0 0 0 2px rgba(82,183,136,0.1)' };
  }
  return {};
}

// ─── UTM helpers ──────────────────────────────────────────────────────────────

function readUtmFromUrl(searchParams: URLSearchParams): Partial<UtmData> {
  return {
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_content: searchParams.get('utm_content') || '',
    utm_term: searchParams.get('utm_term') || '',
  };
}

function loadOrSeedUtmSession(fromUrl: Partial<UtmData>): UtmData {
  try {
    const stored = sessionStorage.getItem(SESSION_UTM_KEY);
    if (stored) {
      return JSON.parse(stored) as UtmData;
    }
  } catch {
    // sessionStorage unavailable — proceed without
  }

  const data: UtmData = {
    utm_source: fromUrl.utm_source || '',
    utm_medium: fromUrl.utm_medium || '',
    utm_campaign: fromUrl.utm_campaign || '',
    utm_content: fromUrl.utm_content || '',
    utm_term: fromUrl.utm_term || '',
    landing_page: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
  };

  try {
    sessionStorage.setItem(SESSION_UTM_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }

  return data;
}

function loadOrSeedPromoSession(fromUrl: string): string {
  if (fromUrl) {
    try {
      sessionStorage.setItem(SESSION_PROMO_KEY, fromUrl);
    } catch {
      // ignore
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(SESSION_PROMO_KEY) || '';
  } catch {
    return '';
  }
}

// ─── Main form component ──────────────────────────────────────────────────────

function ContactFormInner() {
  const searchParams = useSearchParams();
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // UTM attribution state
  const [utmData, setUtmData] = useState<UtmData>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    landing_page: '',
    referrer: '',
  });

  // LOI state
  const [hasLoi, setHasLoi] = useState(false);
  const [loiFile, setLoiFile] = useState<File | null>(null);
  const [loiTooltipVisible, setLoiTooltipVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Seed UTM + promo from URL / sessionStorage on mount
  useEffect(() => {
    const urlUtm = readUtmFromUrl(searchParams as unknown as URLSearchParams);
    const utm = loadOrSeedUtmSession(urlUtm);
    setUtmData(utm);

    const promoFromUrl = searchParams.get('promo_code') || '';
    const promo = loadOrSeedPromoSession(promoFromUrl);

    const industryParam = searchParams.get('industry');

    setForm((prev) => ({
      ...prev,
      promoCode: promo,
      ...(industryParam && industryOptions.includes(industryParam) ? { industry: industryParam } : {}),
    }));
  }, [searchParams]);

  // Re-validate whenever form changes
  useEffect(() => {
    const newErrors = validateForm(form);
    setErrors(newErrors);
  }, [form]);

  const markTouched = (name: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === 'phoneNumber') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length === 0) {
        formattedValue = '';
      } else if (digits.length <= 3) {
        formattedValue = `(${digits}`;
      } else if (digits.length <= 6) {
        formattedValue = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        formattedValue = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
    }
    setForm((prev) => ({
      ...prev,
      [name]: formattedValue,
      ...(name === 'industry' ? { dealDescription: '' } : {}),
    }));
    markTouched(name as keyof FormData);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    markTouched(e.target.name as keyof FormData);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: formatNumberWithCommas(value),
    }));
    markTouched(name as keyof FormData);
  };

  const handleCurrencyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    markTouched(e.target.name as keyof FormData);
  };

  const handleIndustrySelect = (label: string) => {
    setForm((prev) => ({ ...prev, industry: label, dealDescription: '' }));
    setTouched((prev) => ({ ...prev, industry: true, dealDescription: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setSubmitError(null);

    const allTouched: TouchedFields = {
      firstName: true, lastName: true, email: true, phoneNumber: true,
      industry: true, dealDescription: true,
      purchasePrice: true, emdAmount: true, closingDate: true,
    };
    setTouched(allTouched);

    const currentErrors = validateForm(form);
    if (Object.keys(currentErrors).length > 0) {
      trackEvent('contact_form_validation_error', { industry: form.industry });
      return;
    }

    setSubmitting(true);

    try {
      let loiFileUrl: string | null = null;

      // Upload LOI file to Supabase Storage if provided
      if (hasLoi && loiFile) {
        const supabase = createClient();
        const fileExt = loiFile.name.split('.').pop();
        const fileName = `loi_${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('contact-loi')
          .upload(fileName, loiFile, { upsert: false });

        if (uploadError) {
          console.error('LOI upload error:', uploadError);
          // Non-blocking — proceed without file URL
        } else if (uploadData) {
          const { data: urlData } = supabase.storage
            .from('contact-loi')
            .getPublicUrl(uploadData.path);
          loiFileUrl = urlData?.publicUrl ?? null;
        }
      }

      // ── Fire analytics event before submission ──────────────────────────────
      trackEvent('contact_form_submit', {
        industry: form.industry,
        deal_type: form.dealDescription,
        has_loi: hasLoi,
        utm_source: utmData.utm_source,
        utm_campaign: utmData.utm_campaign,
        has_promo: !!form.promoCode,
      });

      // ── 1. POST to internal route (Supabase + email) — PRIMARY ──────────────
      const internalResponse = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          hasLoi,
          loiFileUrl,
          // UTM attribution
          utmSource: utmData.utm_source,
          utmMedium: utmData.utm_medium,
          utmCampaign: utmData.utm_campaign,
          utmContent: utmData.utm_content,
          utmTerm: utmData.utm_term,
          landingPage: utmData.landing_page,
          referrer: utmData.referrer,
        }),
      });

      if (!internalResponse.ok) {
        let errorMsg = "We couldn't submit your request. Please try again.";
        try {
          const errData = await internalResponse.json();
          if (errData?.error && typeof errData.error === 'string') {
            errorMsg = errData.error;
          }
        } catch {
          // ignore JSON parse errors
        }
        console.error('Internal submission failed:', internalResponse.status);
        setSubmitError(errorMsg);
        setSubmitting(false);
        return;
      }

      // ── 2. Fire-and-forget CRM submission (non-blocking) ────────────────────
      const crmPayload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phoneNumber.trim(),
        company_name: form.company.trim() || undefined,
        message: form.dealSummary.trim() || undefined,
        industry: form.industry,
        deal_type: form.dealDescription || undefined,
        purchase_price: form.purchasePrice || undefined,
        estimated_emd_amount: form.emdAmount || undefined,
        target_closing_date: form.closingDate || undefined,
        how_heard: form.howHeard || undefined,
        has_loi: hasLoi,
        loi_file_url: loiFileUrl || undefined,
        promo_code: form.promoCode.trim() || undefined,
        landing_page: utmData.landing_page || undefined,
        referrer: utmData.referrer || undefined,
        utm_source: utmData.utm_source || undefined,
        utm_medium: utmData.utm_medium || undefined,
        utm_campaign: utmData.utm_campaign || undefined,
        utm_content: utmData.utm_content || undefined,
        utm_term: utmData.utm_term || undefined,
      };

      fetch(CRM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(crmPayload),
      }).catch((crmErr) => {
        console.warn('CRM submission failed (non-blocking):', crmErr);
      });

      // ── Fire success analytics event ────────────────────────────────────────
      trackEvent('contact_form_success', {
        industry: form.industry,
        deal_type: form.dealDescription,
        has_loi: hasLoi,
        utm_source: utmData.utm_source,
        utm_campaign: utmData.utm_campaign,
        has_promo: !!form.promoCode,
      });

      setSubmitted(true);
    } catch (err: any) {
      console.error('Form submission error:', err);
      trackEvent('contact_form_error', { industry: form.industry });
      setSubmitError("We couldn't submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showError = (field: keyof FormData) =>
    (touched[field] || submitAttempted) ? errors[field] : undefined;

  const currentDealTypes = form.industry ? (dealTypesByIndustry[form.industry] ?? dealTypesByIndustry['Other']) : [];
  const dealPlaceholder = form.industry
    ? (dealDescriptionPlaceholders[form.industry] ?? dealDescriptionPlaceholders['Other'])
    : 'Describe your deal and the deposit you need funded...';

  // ── Success state ────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <section
        className="py-20 px-6 relative z-10"
        style={{ background: 'var(--color-surface)' }}
      >
        <div className="max-w-2xl mx-auto">
          <div
            className="rounded-3xl overflow-hidden"
            style={{
              background: 'var(--color-bg)',
              border: '1px solid rgba(27,67,50,0.1)',
              boxShadow: '0 32px 80px rgba(27,67,50,0.1)',
            }}
          >
            <div
              className="h-1.5 w-full"
              style={{
                background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 60%, var(--color-gold) 100%)',
              }}
            />

            <div className="p-10 md:p-14 text-center">
              <div className="flex justify-center mb-8">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse-glow"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                  }}
                >
                  <svg
                    className="w-9 h-9 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <p
                className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3"
                style={{ color: 'var(--color-accent)' }}
              >
                Submission Confirmed
              </p>

              <h2
                className="font-display font-semibold mb-4"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Thanks — we received your request.
              </h2>

              <p
                className="text-base leading-relaxed mb-10 max-w-md mx-auto"
                style={{ color: 'var(--color-muted)' }}
              >
                An EarnestBridge team member will be in touch shortly. Our team reviews every submission and responds within{' '}
                <strong style={{ color: 'var(--color-primary)' }}>2–4 business hours</strong>.
              </p>

              <div
                className="grid grid-cols-3 gap-px rounded-2xl overflow-hidden mb-10"
                style={{ background: 'rgba(27,67,50,0.08)' }}
              >
                {[
                  { value: '2–4 hrs', label: 'Response Time' },
                  { value: '0', label: 'Personal Guarantee' },
                  { value: '100%', label: 'Confidential' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="py-5 px-4 text-center"
                    style={{ background: 'var(--color-bg)' }}
                  >
                    <p
                      className="font-display font-semibold mb-0.5"
                      style={{
                        fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                        color: 'var(--color-primary)',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {stat.value}
                    </p>
                    <p
                      className="text-[10px] font-semibold uppercase tracking-[0.2em]"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href="/how-it-works"
                  className="btn-primary text-sm font-bold uppercase tracking-[0.15em] px-8 py-3.5 rounded-full w-full sm:w-auto text-center"
                >
                  Learn More About the Process
                </Link>
                <Link
                  href="/homepage"
                  className="text-sm font-medium transition-colors hover:underline"
                  style={{ color: 'var(--color-muted)' }}
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>

          <p
            className="text-center text-xs mt-6"
            style={{ color: 'var(--color-muted)' }}
          >
            Questions? Email us at{' '}
            <a
              href="mailto:deals@earnestbridge.com"
              className="font-semibold hover:underline"
              style={{ color: 'var(--color-primary)' }}
            >
              deals@earnestbridge.com
            </a>
          </p>
        </div>
      </section>
    );
  }

  // ── Form state ───────────────────────────────────────────────────────────────
  return (
    <section
      className="py-16 pb-28 px-6 relative z-10"
      style={{ background: 'var(--color-surface)' }}
      aria-labelledby="contact-form-heading"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Left: Trust Signals */}
          <aside className="lg:col-span-2">
            <div className="sticky top-28">
              <p className="section-label mb-4">Why EarnestBridge</p>
              <h2
                id="contact-form-heading"
                className="font-display font-semibold mb-8"
                style={{
                  fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                }}
              >
                Submit your deal.
                <br />
                <span className="italic" style={{ color: 'var(--color-primary)' }}>
                  We handle the rest.
                </span>
              </h2>

              <div className="space-y-6 mb-10">
                {trustSignals.map((signal) => (
                  <div key={signal.title} className="flex items-start gap-4">
                    <span className="text-2xl mt-0.5">{signal.icon}</span>
                    <div>
                      <p
                        className="text-sm font-semibold mb-1"
                        style={{ color: 'var(--color-foreground)' }}
                      >
                        {signal.title}
                      </p>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        {signal.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Industry quick links */}
              <div
                className="rounded-2xl p-6 mb-6"
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid rgba(27,67,50,0.1)',
                }}
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.3em] mb-4"
                  style={{ color: 'var(--color-muted)' }}
                >
                  Industries We Serve
                </p>
                <div className="flex flex-wrap gap-2">
                  {['🏢 CRE', '🤝 M&A', '✈️ Aviation', '🏗️ Gov', '⛽ Energy', '⛵ Maritime'].map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
                      style={{
                        background: 'rgba(82,183,136,0.1)',
                        color: 'var(--color-primary)',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Direct contact */}
              <div
                className="rounded-2xl p-6"
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid rgba(27,67,50,0.1)',
                }}
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.3em] mb-4"
                  style={{ color: 'var(--color-muted)' }}
                >
                  Prefer to Talk?
                </p>
                <a
                  href="mailto:deals@earnestbridge.com"
                  className="flex items-center gap-3 group"
                >
                  <span className="text-lg">✉️</span>
                  <div>
                    <p
                      className="text-sm font-bold group-hover:underline"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      deals@earnestbridge.com
                    </p>
                    <p
                      className="text-[10px] font-medium uppercase tracking-[0.2em]"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Responds same business day
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </aside>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <p className="text-xs mb-5" style={{ color: 'var(--color-muted)' }}>
              Fields marked with <span style={{ color: '#DC2626' }}>*</span> are required.
            </p>

            <form onSubmit={handleSubmit} noValidate aria-label="Deal submission form">

              {/* Contact Info Block */}
              <div
                className="rounded-2xl p-8 mb-6"
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid rgba(27,67,50,0.1)',
                }}
              >
                <h3
                  className="font-display font-semibold text-lg mb-6"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  Your Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      First Name <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={form.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="John"
                      className="form-input-box"
                      style={inputStyle(!!errors.firstName, !!touched.firstName, form.firstName)}
                      aria-invalid={!!showError('firstName')}
                      aria-describedby={showError('firstName') ? 'firstName-error' : undefined}
                    />
                    <span id="firstName-error"><FieldError message={showError('firstName')} /></span>
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Last Name <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={form.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Smith"
                      className="form-input-box"
                      style={inputStyle(!!errors.lastName, !!touched.lastName, form.lastName)}
                      aria-invalid={!!showError('lastName')}
                      aria-describedby={showError('lastName') ? 'lastName-error' : undefined}
                    />
                    <span id="lastName-error"><FieldError message={showError('lastName')} /></span>
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Email Address <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="you@yourcompany.com"
                      className="form-input-box"
                      style={inputStyle(!!errors.email, !!touched.email, form.email)}
                      aria-invalid={!!showError('email')}
                      aria-describedby={showError('email') ? 'email-error' : undefined}
                    />
                    <span id="email-error"><FieldError message={showError('email')} /></span>
                  </div>
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Phone Number <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      required
                      value={form.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="(000) 000-0000"
                      className="form-input-box"
                      style={inputStyle(!!errors.phoneNumber, !!touched.phoneNumber, form.phoneNumber)}
                      aria-invalid={!!showError('phoneNumber')}
                      aria-describedby={showError('phoneNumber') ? 'phoneNumber-error' : undefined}
                    />
                    <span id="phoneNumber-error"><FieldError message={showError('phoneNumber')} /></span>
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Company / Entity Name
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your Company LLC"
                      className="form-input-box"
                    />
                  </div>
                </div>
              </div>

              {/* Industry Selector Block */}
              <div
                className="rounded-2xl p-8 mb-6"
                style={{
                  background: 'var(--color-bg)',
                  border: showError('industry')
                    ? '1.5px solid #DC2626' : '1px solid rgba(27,67,50,0.1)',
                }}
              >
                <h3
                  className="font-display font-semibold text-lg mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  Industry Vertical <span style={{ color: '#DC2626' }}>*</span>
                </h3>
                <p
                  className="text-sm mb-6"
                  style={{ color: 'var(--color-muted)' }}
                >
                  Select the industry your deal falls under. This helps us route your submission to the right specialist.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Commercial Real Estate', icon: '🏢' },
                    { label: 'Business Acquisitions (M&A)', icon: '🤝' },
                    { label: 'Private Aviation', icon: '✈️' },
                    { label: 'Government & Infrastructure Tendering', icon: '🏗️' },
                    { label: 'Oil, Gas & Mineral Leasing', icon: '⛽' },
                    { label: 'Luxury Maritime (Yachts)', icon: '⛵' },
                    { label: 'Other', icon: '📋' },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => handleIndustrySelect(opt.label)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl text-center transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        background: form.industry === opt.label ? 'rgba(82,183,136,0.12)' : 'var(--color-surface)',
                        border: form.industry === opt.label
                          ? '1.5px solid rgba(82,183,136,0.5)'
                          : '1.5px solid rgba(27,67,50,0.1)',
                        cursor: 'pointer',
                      }}
                      aria-pressed={form.industry === opt.label}
                    >
                      <span className="text-2xl">{opt.icon}</span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-[0.15em] leading-tight"
                        style={{
                          color: form.industry === opt.label ? 'var(--color-primary)' : 'var(--color-muted)',
                        }}
                      >
                        {opt.label === 'Government & Infrastructure Tendering' ? 'Gov & Infrastructure' :
                         opt.label === 'Business Acquisitions (M&A)' ? 'Business M&A' :
                         opt.label === 'Oil, Gas & Mineral Leasing' ? 'Oil, Gas & Minerals' :
                         opt.label === 'Luxury Maritime (Yachts)' ? 'Luxury Maritime' :
                         opt.label}
                      </span>
                    </button>
                  ))}
                </div>
                <input type="hidden" name="industry" value={form.industry} />
                {showError('industry') && (
                  <div className="mt-3">
                    <FieldError message={showError('industry')} />
                  </div>
                )}
              </div>

              {/* Deal Details Block */}
              <div
                className="rounded-2xl p-8 mb-6"
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid rgba(27,67,50,0.1)',
                }}
              >
                <h3
                  className="font-display font-semibold text-lg mb-6"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  Deal Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Deal type — shown only when industry is selected */}
                  {form.industry && currentDealTypes.length > 0 && (
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="dealDescription"
                        className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        Deal Type <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <select
                        id="dealDescription"
                        name="dealDescription"
                        required
                        value={form.dealDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-input-box"
                        style={{
                          appearance: 'none',
                          cursor: 'pointer',
                          ...inputStyle(!!errors.dealDescription, !!touched.dealDescription, form.dealDescription),
                        }}
                        aria-invalid={!!showError('dealDescription')}
                        aria-describedby={showError('dealDescription') ? 'dealDescription-error' : undefined}
                      >
                        <option value="" disabled>
                          Select deal type
                        </option>
                        {currentDealTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <span id="dealDescription-error"><FieldError message={showError('dealDescription')} /></span>
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="purchasePrice"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Purchase / Deal Price <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <div
                      className="flex items-center form-input-box"
                      style={{
                        padding: 0,
                        ...inputStyle(!!errors.purchasePrice, !!touched.purchasePrice, form.purchasePrice),
                      }}
                    >
                      <span
                        className="pl-4 pr-1 text-sm font-medium pointer-events-none flex-shrink-0"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        $
                      </span>
                      <input
                        id="purchasePrice"
                        name="purchasePrice"
                        type="text"
                        inputMode="numeric"
                        required
                        value={form.purchasePrice}
                        onChange={handleCurrencyChange}
                        onBlur={handleCurrencyBlur}
                        placeholder="4,500,000"
                        className="flex-1 bg-transparent outline-none border-none py-[0.875rem] pr-[1.1rem] text-[0.9rem]"
                        style={{ fontFamily: 'var(--font-body)', color: 'var(--color-foreground)' }}
                        aria-invalid={!!showError('purchasePrice')}
                        aria-describedby={showError('purchasePrice') ? 'purchasePrice-error' : undefined}
                      />
                    </div>
                    <span id="purchasePrice-error"><FieldError message={showError('purchasePrice')} /></span>
                  </div>

                  <div>
                    <label
                      htmlFor="emdAmount"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      EMD / Deposit Amount Needed <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <div
                      className="flex items-center form-input-box"
                      style={{
                        padding: 0,
                        ...inputStyle(!!errors.emdAmount, !!touched.emdAmount, form.emdAmount),
                      }}
                    >
                      <span
                        className="pl-4 pr-1 text-sm font-medium pointer-events-none flex-shrink-0"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        $
                      </span>
                      <input
                        id="emdAmount"
                        name="emdAmount"
                        type="text"
                        inputMode="numeric"
                        required
                        value={form.emdAmount}
                        onChange={handleCurrencyChange}
                        onBlur={handleCurrencyBlur}
                        placeholder="250,000"
                        className="flex-1 bg-transparent outline-none border-none py-[0.875rem] pr-[1.1rem] text-[0.9rem]"
                        style={{ fontFamily: 'var(--font-body)', color: 'var(--color-foreground)' }}
                        aria-invalid={!!showError('emdAmount')}
                        aria-describedby={showError('emdAmount') ? 'emdAmount-error' : undefined}
                      />
                    </div>
                    <span id="emdAmount-error"><FieldError message={showError('emdAmount')} /></span>
                  </div>

                  <div>
                    <label
                      htmlFor="closingDate"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Target Close / Deadline <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      id="closingDate"
                      name="closingDate"
                      type="date"
                      required
                      value={form.closingDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-input-box"
                      style={inputStyle(!!errors.closingDate, !!touched.closingDate, form.closingDate)}
                      aria-invalid={!!showError('closingDate')}
                      aria-describedby={showError('closingDate') ? 'closingDate-error' : undefined}
                    />
                    <span id="closingDate-error"><FieldError message={showError('closingDate')} /></span>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="dealSummary"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Brief Deal Summary{' '}
                      <span className="normal-case font-normal tracking-normal text-[10px]" style={{ color: 'var(--color-muted)' }}>
                        (Optional)
                      </span>
                    </label>
                    <textarea
                      id="dealSummary"
                      name="dealSummary"
                      rows={3}
                      value={form.dealSummary}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={dealPlaceholder}
                      className="form-input-box resize-none"
                    />
                  </div>

                  {/* LOI Checkbox */}
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          id="hasLoi"
                          checked={hasLoi}
                          onChange={(e) => {
                            setHasLoi(e.target.checked);
                            if (!e.target.checked) {
                              setLoiFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }
                          }}
                          className="sr-only"
                        />
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center transition-all duration-150"
                          style={{
                            background: hasLoi ? 'var(--color-primary)' : 'var(--color-surface)',
                            border: hasLoi ? '1.5px solid var(--color-primary)' : '1.5px solid rgba(27,67,50,0.25)',
                          }}
                          aria-hidden="true"
                        >
                          {hasLoi && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span
                        className="text-sm font-medium leading-snug select-none"
                        style={{ color: 'var(--color-foreground)' }}
                      >
                        Do you have a signed LOI?
                      </span>
                    </label>

                    {/* LOI File Upload */}
                    {hasLoi && (
                      <div className="mt-4 ml-8">
                        <div className="flex items-center gap-2 mb-2">
                          <label
                            htmlFor="loiFile"
                            className="block text-[11px] font-bold uppercase tracking-[0.2em]"
                            style={{ color: 'var(--color-muted)' }}
                          >
                            Upload LOI{' '}
                            <span className="normal-case font-normal tracking-normal text-[10px]" style={{ color: 'var(--color-muted)' }}>
                              (Optional)
                            </span>
                          </label>
                          <div className="relative">
                            <button
                              type="button"
                              aria-label="More info about LOI upload"
                              onMouseEnter={() => setLoiTooltipVisible(true)}
                              onMouseLeave={() => setLoiTooltipVisible(false)}
                              onFocus={() => setLoiTooltipVisible(true)}
                              onBlur={() => setLoiTooltipVisible(false)}
                              className="flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold transition-colors"
                              style={{
                                background: 'rgba(82,183,136,0.15)',
                                color: 'var(--color-primary)',
                                border: '1px solid rgba(82,183,136,0.3)',
                              }}
                            >
                              ?
                            </button>
                            {loiTooltipVisible && (
                              <div
                                className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-56 text-xs leading-relaxed rounded-xl px-3 py-2.5 shadow-lg pointer-events-none"
                                style={{
                                  background: 'var(--color-foreground)',
                                  color: 'var(--color-bg)',
                                  whiteSpace: 'normal',
                                }}
                                role="tooltip"
                              >
                                Uploading your LOI isn&apos;t required, but helps us get started faster and can speed up our initial review.
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className="relative rounded-xl border-2 border-dashed transition-colors duration-150 px-5 py-4"
                          style={{
                            borderColor: loiFile ? 'rgba(82,183,136,0.5)' : 'rgba(27,67,50,0.18)',
                            background: loiFile ? 'rgba(82,183,136,0.04)' : 'var(--color-surface)',
                          }}
                        >
                          <input
                            ref={fileInputRef}
                            id="loiFile"
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => {
                              const file = e.target.files?.[0] ?? null;
                              setLoiFile(file);
                            }}
                            aria-label="Upload signed LOI document"
                          />
                          <div className="flex items-center gap-3 pointer-events-none">
                            <span className="text-xl">{loiFile ? '📄' : '📎'}</span>
                            <div>
                              {loiFile ? (
                                <p className="text-sm font-medium" style={{ color: 'var(--color-primary)' }}>
                                  {loiFile.name}
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                                    Click to upload or drag &amp; drop
                                  </p>
                                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-muted)' }}>
                                    PDF, Word, or image — max 10 MB
                                  </p>
                                </>
                              )}
                            </div>
                            {loiFile && (
                              <button
                                type="button"
                                className="ml-auto text-xs font-medium underline pointer-events-auto"
                                style={{ color: 'var(--color-muted)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLoiFile(null);
                                  if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="howHeard"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      How Did You Hear About Us?
                    </label>
                    <select
                      id="howHeard"
                      name="howHeard"
                      value={form.howHeard}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-input-box"
                      style={{ appearance: 'none', cursor: 'pointer' }}
                    >
                      <option value="" disabled>
                        Select an option
                      </option>
                      {howHeardOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Promo Code field */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="promoCode"
                      className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-2"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      Promo Code{' '}
                      <span className="normal-case font-normal tracking-normal text-[10px]" style={{ color: 'var(--color-muted)' }}>
                        (Optional)
                      </span>
                    </label>
                    <input
                      id="promoCode"
                      name="promoCode"
                      type="text"
                      value={form.promoCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. BPCON26-A7K4Q2"
                      className="form-input-box"
                      autoComplete="off"
                      style={
                        form.promoCode
                          ? { borderColor: 'rgba(82,183,136,0.6)', boxShadow: '0 0 0 2px rgba(82,183,136,0.1)' }
                          : {}
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Submission error banner */}
              {submitError && (
                <div
                  className="rounded-xl px-5 py-4 mb-6 flex items-start gap-3"
                  style={{
                    background: 'rgba(220,38,38,0.06)',
                    border: '1px solid rgba(220,38,38,0.2)',
                  }}
                  role="alert"
                >
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#DC2626' }} aria-hidden="true">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium" style={{ color: '#DC2626' }}>
                    {submitError}
                  </p>
                </div>
              )}

              {/* Submit */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <p
                  className="text-xs leading-relaxed max-w-xs"
                  style={{ color: 'var(--color-muted)' }}
                >
                  By submitting, you agree to our{' '}
                  <Link
                    href="/privacy-policy"
                    className="underline hover:text-foreground transition-colors"
                  >
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/terms"
                    className="underline hover:text-foreground transition-colors"
                  >
                    Terms of Service
                  </Link>
                  .
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary text-sm font-bold uppercase tracking-[0.15em] px-10 py-4 rounded-full whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-3"
                >
                  {submitting ? (
                    <>
                      <span
                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                        aria-hidden="true"
                      />
                      Submitting...
                    </>
                  ) : (
                    'Submit My Deal →'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ContactForm() {
  return (
    <Suspense fallback={<div className="py-20 px-6" />}>
      <ContactFormInner />
    </Suspense>
  );
}